//---------------CORE--------------------//
import React, { useState, useEffect, Fragment } from "react";
import { useSnackbar } from "notistack";
//---------------MUI--------------------//
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import { useSelector } from "react-redux";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import Badge from "@mui/material/Badge";
//---------------MUI ICONS--------------------//
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SyncIcon from "@mui/icons-material/Sync";
import AddCircleIcon from "@mui/icons-material/AddCircle";
//---------------EXTERNAL--------------------//
import Loader from "components/Progress";
import Popup from "components/Asset View/Controlling/Scheduler/Popup";
import Accordion from "./Accordion";
import noData from "assets/img/no-data.png";
import {
  useGetSchedulesGlobalQuery,
  useGetPendingGlobalQuery,
  useSyncScheduleGlobalMutation,
} from "services/controllingGlobal";
import {
  buildSchedulesParams,
  buildPendingCountParams,
  parseControllingGlobalArgs,
  normaliseFilter,
} from "Utilities/Controlling Global Query";
import { Grid, Paper } from "@mui/material";
import styled from "@emotion/styled";
const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

export default function ControlledAccordions(props) {
  let token = window.localStorage.getItem("token");
  const [expanded, setExpanded] = React.useState(false);
  const metaDataValue = useSelector((state) => state.metaData);
  const { enqueueSnackbar } = useSnackbar();
  const [scheduleState, setScheduleState] = useState([]);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(null);
  const [disabled, setDisable] = useState(false);
  const filtersValue = useSelector((state) => state.filterDevice);
  const [syncSchedules, syncResult] = useSyncScheduleGlobalMutation();
  const [row, setRow] = useState(null);
  const [openPopup, setOpenPopup] = React.useState(false);

  // Reset pagination + clear the accumulator when the parent solution
  // (props.id), the group filter, or the asset-type filter changes.
  // Without this, scrolling past page 1 in group A and then switching to
  // group B keeps `page` at its prior value (e.g. 2), so the success
  // branch at `if (page == 1) temp = [] else temp = [...scheduleState]`
  // appends the new group's rows on top of stale prior-group rows in
  // `scheduleState` — the cross-group leak Rensair reported.
  // - props.id in deps is defensive insurance against future routing
  //   refactors that could keep this component mounted across solution
  //   changes (today the Switch in layouts/services.js always remounts).
  // - The functional setScheduleState bails out via Object.is when the
  //   list is already empty, avoiding a wasted re-render on mount.
  useEffect(() => {
    setPage(1);
    setScheduleState((s) => (s.length === 0 ? s : []));
  }, [props.id, filtersValue.group.id, filtersValue.assetTypes]);

  useEffect(() => {
    if (syncResult.isSuccess) {
      // FANOUT-1: read structured `{status, total, failureCount, failures}`
      // payload and surface partial failure as `warning` variant.
      // See Default Controls/index.js for full rationale.
      const payload = syncResult.data?.payload;
      const variant = payload?.status === "partial" ? "warning" : "success";
      showSnackbar("Automation", syncResult.data?.message, variant, 1000);
      updateSchFn("SYNC");
    }
    if (syncResult.isError) {
      showSnackbar(
        "Automation",
        syncResult.error?.data?.message,
        "error",
        1000
      );
    }
  }, [syncResult]);

  // Takes `currentPage` explicitly (not via closure) so the success
  // effect can pass the parsed request page — closes the last residual
  // closure-staleness surface on cache-hit filter swaps where closure
  // `page` would otherwise be one render behind reqPage.
  function setDisableStat(totalPages, currentPage) {
    if (!totalPages || currentPage >= totalPages) {
      setDisable(true);
    } else setDisable(false);
  }

  function handleMore() {
    if (page < totalPages) {
      setPage(page + 1);
      if (page + 1 >= totalPages) {
        setDisable(true);
      }
    }
  }

  function chkGroup() {
    let permission = metaDataValue.services
      .find((s) => s.id == props.id)
      .tabs.find((tab) => tab.name == "Controlling")?.permission;
    return permission || "DISABLE";
  }

  const handlepopupOpen = () => {
    setOpenPopup(true);
  };

  // Query params are built via the shared Utilities/Controlling Global Query
  // helpers, which omit empty/null filter values from the URL so the BE
  // never sees literal `&groupId=&assetTypes=null`. Centralising the
  // builder also closes the parity-drift surface that originally let ESB
  // diverge from AQM/Default and quietly drop the assetTypes param.
  const schedules = useGetSchedulesGlobalQuery({
    token,
    params: buildSchedulesParams({
      serviceId: props.id,
      page,
      groupId: filtersValue.group.id,
      assetTypes: filtersValue.assetTypes,
      addGroupNames: true,
    }),
  });

  const pendingCount = useGetPendingGlobalQuery({
    token,
    params: buildPendingCountParams({
      serviceId: props.id,
      groupId: filtersValue.group.id,
    }),
  });

  function showSnackbar(title, message, variant, timeOut) {
    return enqueueSnackbar({ title, message, variant }, { timeOut });
  }

  function convertUTCDateToLocalDate(date) {
    const time = date.split("T")[1].slice(0, -1)
    const [hours, mins, ...sec] = time.split(":")
    const newDate = new Date().setUTCHours(hours, mins, '0')
    return new Date(newDate).toLocaleTimeString()
  }

  function appendTimezonetoTime(date, timezone) {
    if(timezone){
      const time = date.split("T")[1].slice(0,-1)
      const [hours, mins, ...sec] = time.split(":")
      let timezoneOffset = 0
      let timezoneAdjustment = "+"
      if(timezone && timezone !== "UTC"){
        timezoneOffset = parseInt(timezone.slice(4))
        timezoneAdjustment = timezone.slice(3,4)
      }
      let UTCHours = timezoneAdjustment == "+" ? parseInt(hours) + timezoneOffset : parseInt(hours) - timezoneOffset
      if(UTCHours < 0){
        UTCHours = 24 + UTCHours
      }
      if(UTCHours > 24) {
        UTCHours = UTCHours - 24
      }
      if(UTCHours == 24) {
        UTCHours = "00"
      }
      return `${parseInt(UTCHours)>12 ? parseInt(UTCHours)-12 : UTCHours}:${mins.split("").length == 2 ? mins : "0" + mins}:00 ${UTCHours >= 12 ? "PM" : "AM"} (${timezone})`
    } else {
      const time = date.split("T")[1].slice(0, -1)
      const [hours, mins, ...sec] = time.split(":")
      const newDate = new Date().setUTCHours(hours, mins, '0')
      return new Date(newDate).toLocaleTimeString()
    }
  }

  // Project the response into local pagination state.
  //
  // The prior implementation depended on `[schedules.isFetching]` and
  // captured `scheduleState`/`page` by closure, which had three failure
  // modes the audit identified:
  //   1. Cache-hit transitions (the new filter's data was already cached
  //      → `isFetching` never toggled → the effect did not fire and the
  //      list stayed stale).
  //   2. A stale-closure race where a delayed in-flight response could
  //      land into an outdated `scheduleState` snapshot.
  //   3. No arg-drift guard, so any response routed to the current
  //      subscription was unconditionally appended.
  //
  // The new implementation:
  //   - Depends on `[schedules.data, schedules.fulfilledTimeStamp,
  //     schedules.error]` so both fresh fetches AND cache-hit transitions
  //     (where the data reference changes without `isFetching` toggling)
  //     trigger the effect.
  //   - Uses a functional `setScheduleState(prev => …)` so the append
  //     branch reads the latest state, not a closure snapshot.
  //   - Compares the request's args (extracted from
  //     `schedules.originalArgs.params`) to the currently-selected filter
  //     and silently drops responses for stale args — belt-and-braces on
  //     top of RTK Query's cache-key isolation.
  useEffect(() => {
    if (schedules.isError) {
      showSnackbar("Schedules", schedules.error?.data?.message, "error");
      return;
    }
    if (!schedules.isSuccess || !schedules.data?.payload) return;

    // Arg-drift guard: silently drop responses whose request was issued
    // for a serviceId/groupId/assetTypes/currentPage that no longer match
    // the live filter+pager. The serviceId comparison makes this
    // symmetric with the `props.id` dep on the reset effect, hardening
    // against a future routing refactor that keeps the component mounted
    // across solution changes.
    //
    // currentPage is part of the guard for an important race: when a
    // mutation succeeds the controllingGlobal service invalidates the
    // ["Control"] tag, which fires an automatic refetch at the user's
    // CURRENT page (call it R_N, N>=2). Microseconds later, updateSchFn
    // sets page=1 and triggers a SECOND refetch (R_1). Without the
    // currentPage comparison, an R_N response that lands AFTER R_1 (e.g.
    // because R_1's small-page payload returned faster) would re-enter
    // the success effect, see page===N!==1, and APPEND the original-page
    // rows on top of the just-replaced page-1 accumulator — recreating
    // the exact row-duplication regression the updateSchFn rewrite was
    // meant to eliminate. With the comparison, R_N is dropped silently
    // because the live `page` is now 1.
    //
    // `reqPage` (used below) drives the page===1 replace-vs-append
    // decision instead of closure `page`, so the Load-More button's
    // disabled state cannot flake during cache-hit filter swaps.
    const reqArgs = parseControllingGlobalArgs(schedules.originalArgs?.params);
    if (reqArgs) {
      if (
        reqArgs.serviceId !== normaliseFilter(props.id) ||
        reqArgs.groupId !== normaliseFilter(filtersValue.group.id) ||
        reqArgs.assetTypes !== normaliseFilter(filtersValue.assetTypes) ||
        reqArgs.currentPage !== page
      ) return;
    }
    const reqPage = reqArgs ? reqArgs.currentPage : page;

    const newRows = (schedules.data.payload.data || []).map((elm) => {
      const dateDays = [];
      if (elm.days.length > 0) {
        elm.days.forEach((days) => {
          dateDays.push(days);
        });
      } else {
        dateDays.push(new Date(elm.time).toLocaleDateString());
      }
      return {
        groupName: elm?.groupName,
        name: elm.name,
        baseTime: elm.time,
        time: convertUTCDateToLocalDate(elm.time),
        displayTime: props.timezoneSelection
          ? appendTimezonetoTime(elm.time, elm.timezone)
          : convertUTCDateToLocalDate(elm.time),
        html2: dateDays,
        actuator: elm.actuatorName,
        command: elm.command,
        commandName: elm.commandKey,
        id: elm.globalId,
        syncStatus: elm?.syncStatus ? elm?.syncStatus : false,
        pending: elm?.pending ? elm?.pending : false,
        synced: elm?.synced ? elm?.synced : false,
        notifyExecutionStatus: elm?.notifyExecutionStatus,
        notifyExecutionStatusEmail: elm?.notifyExecutionStatusEmail,
        notifyExecutionStatusReportAfter: elm?.notifyExecutionStatusReportAfter,
        enabled: elm?.enabled,
        timezone: elm?.timezone,
      };
    });

    setDisableStat(schedules.data?.payload?.totalPages, reqPage);
    setTotalPages(schedules.data.payload.totalPages);
    // page === 1 REPLACE, else APPEND.
    //
    // Both prior schemes (globalId-only dedup-merge, composite-key
    // dedup-merge) regressed under SYNC's flag-flip: the composite-key
    // version had prev rows carrying OLD-flag composite keys and
    // newRows carrying NEW-flag composite keys, so
    // `newKeys.has(prevKey)` was always false — the filter retained
    // EVERY stale row and appended every fresh row, visibly doubling
    // page-N. The simpler scheme below (plain APPEND on page>1)
    // combined with updateSchFn unconditionally resetting to page 1
    // on every mutation (including SYNC) guarantees the success
    // effect always lands in the REPLACE branch on any
    // post-mutation refetch. Pure Load-More (page>1) gets a clean
    // append over distinct-id rows.
    //
    // The only residual edge is the BE getGlobal aggregation
    // split-sibling-docs-across-pages case (one globalId emitted
    // twice with divergent flags straddling pages N and N+1). Plain
    // APPEND shows both copies on Load-More — preferable to the
    // composite-key bug it replaces, and the only fully-correct fix
    // is a BE-side merge anyway.
    setScheduleState((prev) =>
      reqPage === 1 ? newRows : [...prev, ...newRows]
    );
  }, [schedules.data, schedules.fulfilledTimeStamp, schedules.error]);

  useEffect(() => {
    setScheduleState((prevState) => prevState.map((row) => {
      return {
        ...row,
        displayTime: props.timezoneSelection ? appendTimezonetoTime(row.baseTime, row.timezone) : convertUTCDateToLocalDate(row.baseTime),
      }
    }))
  }, [props.timezoneSelection])

  // Callbacks the popup (Add/Edit/Delete) and the Sync result-effect
  // notify us through. `state` is unused under the current model;
  // every mutation resets to page 1 so the invalidation-driven
  // refetch lands in the success-effect's page===1 REPLACE branch.
  //
  // We do NOT clear scheduleState here — that would render the empty
  // "No schedules found" graphic if the refetch fails (network blip,
  // BE 500). Keeping the prior rows visible until the refetch settles
  // is more resilient: on success, REPLACE swaps them out cleanly;
  // on failure, the snackbar fires and stale rows remain visible
  // (correct UX given the mutation itself succeeded).
  //
  // SYNC was previously exempted to preserve pagination context, but
  // that combined with the success effect's dedup-merge produced the
  // duplication bug the prior audits surfaced — the UX cost of SYNC
  // bouncing to page 1 is the smaller compromise.
  async function updateSchFn(/* state, payload — unused */) {
    setPage(1);
  }

  return (
    <div
      style={{
        width: "100%",
      }}
    >
      {openPopup ? (
        <Popup
          actuators={props.actuators}
          setOpenPopup={setOpenPopup}
          row={row}
          setRow={setRow}
          id={props.id}
          group={filtersValue.group.id}
          global
          updateSchFn={updateSchFn}
        />
      ) : null}
      {chkGroup() == "ALL" ? (
        <span
          style={{
            position: "absolute",
            top: "5px",
            right: "40px",
          }}
        >
          {/* <Tooltip
            title="Sync Automations"
            placement="bottom"
            arrow
            TransitionComponent={Zoom}
          >
            <IconButton
              color="secondary"
              disabled={
                pendingCount.data?.payload?.pendingCount
                  ? pendingCount.data?.payload?.pendingCount.length < 1
                  : true
              }
              onClick={() =>
                syncSchedules({
                  token,
                  body: {
                    serviceId: props.id,
                    groupId: filtersValue.group.id,
                  },
                })
              }
            >
              <Badge
                badgeContent={pendingCount.data?.payload?.pendingCount}
                color="warning"
              >
                <SyncIcon />
              </Badge>
            </IconButton>
          </Tooltip> */}

          <Tooltip
            title="Add Automation"
            placement="bottom"
            arrow
            TransitionComponent={Zoom}
          >
            <IconButton color="secondary" onClick={handlepopupOpen}>
              <AddCircleIcon />
            </IconButton>
          </Tooltip>
        </span>
      ) : null}

      {schedules.isFetching ? (
        <div
          style={{
            height: "calc(100vh - 700px)",
            minHeight: "100px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loader />
        </div>
      ) : (
        <Fragment>
          {scheduleState.length == 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                paddingBottom: "40px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "200px",
                  width: "200px",
                }}
              >
                <img
                  style={{ maxWidth: "70%", maxHeight: "70%" }}
                  src={noData}
                />
              </div>
              <p style={{ color: "#c8c8c8" }}>No schedules found</p>
            </div>
          ) : (
            <Fragment>
              <div
                style={{
                  height: !props.fullScreenModeOpen ? "calc(100vh - 700px)" : '72vh',
                  minHeight: !props.fullScreenModeOpen ? "100px" : '100%',
                  overflowY: "scroll",
                }}
              >
                {props.fullScreenModeOpen ? (
                <Grid container spacing={2}>
                {scheduleState.map((elm, i) => (
                    <Grid item xs={12} sm={6} md={4} key={i} >
                      <Item style={{padding:'0px'}}>
                        <Accordion
                          expanded={expanded}
                          setExpanded={setExpanded}
                          index={i}
                          id={props.id}
                          history={props.history}
                          actuators={props.actuators}
                          group={filtersValue.group.id}
                          permission={chkGroup()}
                          row={elm}
                          setRow={setRow}
                          setOpenPopup={setOpenPopup}
                          fullScreenModeOpen={props.fullScreenModeOpen}
                          updateSchFn={updateSchFn}
                        />
                      </Item>
                    </Grid>
                ))}
                </Grid>
                ) : (
                  scheduleState.map((elm, i) => (
                        <Accordion
                          expanded={expanded}
                          setExpanded={setExpanded}
                          index={i}
                          id={props.id}
                          history={props.history}
                          actuators={props.actuators}
                          group={filtersValue.group.id}
                          permission={chkGroup()}
                          row={elm}
                          setRow={setRow}
                          setOpenPopup={setOpenPopup}
                          fullScreenModeOpen={props.fullScreenModeOpen}
                          updateSchFn={updateSchFn}
                        />
                  ))
                )}
                {scheduleState.length != 0 && !disabled ? (
                  <span
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginTop: "15px",
                    }}
                  >
                    {schedules.isFetching ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                          height: "46px",
                          width: "46px",
                          backgroundColor:
                            metaDataValue.branding.secondaryColor,
                        }}
                      >
                        <CircularProgress
                          size={20}
                          style={{ color: "white" }}
                        />
                      </div>
                    ) : (
                      <IconButton
                        color="secondary"
                        onClick={handleMore}
                        style={{
                          backgroundColor:
                            metaDataValue.branding.secondaryColor,
                        }}
                      >
                        <KeyboardArrowDownIcon
                          style={{
                            cursor: "pointer",
                            height: "30px",
                            width: "30px",
                            color: "white",
                          }}
                        />
                      </IconButton>
                    )}
                  </span>
                ) : null}
              </div>
            </Fragment>
          )}
        </Fragment>
      )}
    </div>
  );
}